import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Icon from 'components/AppIcon';
import { useAuth } from 'contexts/AuthContext';
import { useBranch } from 'contexts/BranchContext';
import api from 'lib/api';

const NAV_ITEMS = [
  {
    label: 'Dashboard',
    path: '/super-admin-dashboard',
    icon: 'LayoutDashboard',
    tooltip: 'Executive Overview - Strategic business health',
    roles: ['super_admin'],
  },
  // {
  //   label: 'Revenue',
  //   path: '/revenue-operations',
  //   icon: 'TrendingUp',
  //   tooltip: 'Revenue Operations - Financial monitoring',
  //   roles: ['super_admin'],
  // },
  // {
  //   label: 'Analytics',
  //   path: '/member-analytics-hub',
  //   icon: 'Users',
  //   tooltip: 'Member Analytics Hub - Retention & engagement',
  //   roles: ['super_admin'],
  // },
  // {
  //   label: 'Super Admin',
  //   path: '/super-admin-dashboard',
  //   icon: 'ShieldCheck',
  //   tooltip: 'Super Admin Dashboard - Multi-tenant vendor management',
  //   roles: ['super_admin'],
  // },
  {
    label: 'Dashboard',
    path: '/gym-owner-portal',
    icon: 'Building2',
    tooltip: 'Gym Owner Portal - Overview & branch management',
    roles: ['vendor'],
  },
  {
    label: 'Members',
    path: '/gym-owner-members',
    icon: 'Users',
    tooltip: 'Members - Registration & management',
    roles: ['vendor'],
  },
  {
    label: 'Classes',
    path: '/gym-owner-classes',
    icon: 'CalendarDays',
    tooltip: 'Classes - Schedule & manage fitness classes',
    roles: ['vendor'],
  },
  {
    label: 'Packages',
    path: '/gym-owner-packages',
    icon: 'Package',
    tooltip: 'Packages - Membership packages & plans',
    roles: ['vendor'],
  },
  {
    label: 'POS',
    path: '/gym-owner-pos',
    icon: 'ShoppingBag',
    tooltip: 'POS / Sales - Point of sale management',
    roles: ['vendor'],
  },
  {
    label: 'Settings',
    path: '/gym-owner-settings',
    icon: 'Settings',
    tooltip: 'Settings - Gym configuration',
    roles: ['vendor'],
  },
  {
    label: 'Staff',
    path: '/staff-trainer-interface',
    icon: 'ClipboardList',
    tooltip: 'Staff & Trainer Interface - Attendance & training programs',
    roles: ['staff', 'trainer', 'vendor'],
  },
  {
    label: 'Trainer',
    path: '/trainer-dashboard',
    icon: 'Dumbbell',
    tooltip: 'Trainer Dashboard - Classes, members, earnings & performance',
    roles: ['trainer'],
  },
  {
    label: 'Member',
    path: '/member-portal',
    icon: 'UserCircle',
    tooltip: 'Member Portal - Membership, plans & invoices',
    roles: ['member'],
  },
  {
    label: 'Progress',
    path: '/member-progress',
    icon: 'Activity',
    tooltip: 'Member Progress - Weight, BMI & goals tracking',
    roles: ['member'],
  },
];


const ROLE_LABELS = {
  super_admin: 'Super Admin',
  vendor: 'Gym Owner',
  staff: 'Staff',
  trainer: 'Trainer',
  member: 'Member',
};

const DATE_PRESETS = [
  { label: 'MTD', value: 'mtd', description: 'Month to Date' },
  { label: 'QTD', value: 'qtd', description: 'Quarter to Date' },
  { label: 'YTD', value: 'ytd', description: 'Year to Date' },
  { label: '30D', value: '30d', description: 'Last 30 Days' },
];

function GymIdBadge({ gymId }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(gymId).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <button
      onClick={handleCopy}
      title={copied ? 'Copied!' : 'Click to copy Gym ID'}
      className="hidden md:flex items-center gap-1.5 px-2.5 h-9 rounded-md border border-border bg-card hover:border-primary/50 transition-colors duration-[150ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
    >
      <Icon name="Hash" size={13} color="var(--color-primary)" />
      <span className="text-xs font-data text-foreground tracking-wide">{gymId}</span>
      <Icon
        name={copied ? 'Check' : 'Copy'}
        size={12}
        color={copied ? 'var(--color-success, #22c55e)' : 'var(--color-muted-foreground)'}
      />
    </button>
  );
}

export default function TopNavigation({
  userRole: userRoleProp = 'gym_owner',
  onRoleChange,
  onTenantChange,
  onDateRangeChange,
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { userRole: authRole, profile, signOut } = useAuth();
  const { branches, selectedBranch, changeSelectedBranch } = useBranch();
  const userRole = authRole || userRoleProp;
  const visibleNavItems = NAV_ITEMS.filter((item) => item.roles.includes(userRole));

  // Branch dropdown is shown only for vendor (with >1 branch); hidden for super_admin, staff, trainer
  const isStaffOrTrainer = ['staff', 'trainer'].includes(userRole);
  const showBranchDropdown = !isStaffOrTrainer && userRole !== 'super_admin' && branches.length > 1;
  // Show a static branch label for staff/trainer when they have an assigned branch
  const showBranchLabel = isStaffOrTrainer && selectedBranch;

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [tenantDropdownOpen, setTenantDropdownOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [billing, setBilling] = useState(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [selectedDatePreset, setSelectedDatePreset] = useState(DATE_PRESETS?.[0]);
  const [connectionStatus, setConnectionStatus] = useState('online');
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [statusTooltipVisible, setStatusTooltipVisible] = useState(false);
  const [tenantSearch, setTenantSearch] = useState('');

  const tenantRef = useRef(null);
  const userMenuRef = useRef(null);
  const dateRef = useRef(null);
  const mobileMenuRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (tenantRef?.current && !tenantRef?.current?.contains(e?.target)) setTenantDropdownOpen(false);
      if (userMenuRef?.current && !userMenuRef?.current?.contains(e?.target)) setUserMenuOpen(false);
      if (dateRef?.current && !dateRef?.current?.contains(e?.target)) setDateDropdownOpen(false);
      if (mobileMenuRef?.current && !mobileMenuRef?.current?.contains(e?.target)) setMobileMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location?.pathname]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e?.key === 'Escape') {
        setTenantDropdownOpen(false);
        setUserMenuOpen(false);
        setDateDropdownOpen(false);
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const handleTenantSelect = (branch) => {
    changeSelectedBranch(branch);
    setTenantDropdownOpen(false);
    setTenantSearch('');
    onTenantChange?.(branch);
  };

  const fetchBilling = async () => {
    if (userRole !== 'vendor' || billing !== null) return;
    setBillingLoading(true);
    try {
      const [subRes, invRes] = await Promise.allSettled([
        api.get('/super-admin/my-subscription'),
        api.get('/super-admin/my-invoices'),
      ]);
      setBilling({
        subscription: subRes.status === 'fulfilled' ? subRes.value?.data : null,
        invoices:     invRes.status === 'fulfilled' ? (invRes.value?.data || []) : [],
      });
    } catch {
      setBilling({ subscription: null, invoices: [] });
    } finally {
      setBillingLoading(false);
    }
  };

  const handleSignOut = async () => {
    setUserMenuOpen(false);
    await signOut();
    navigate('/login-page');
  };

  const getUserInitials = () => {
    const name = profile?.full_name || '';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase() || '??';
  };

  const handleDatePresetSelect = (preset) => {
    setSelectedDatePreset(preset);
    setDateDropdownOpen(false);
    onDateRangeChange?.(preset);
  };

  const handleRefresh = () => {
    setConnectionStatus('syncing');
    setTimeout(() => {
      setConnectionStatus('online');
      setLastUpdated(new Date());
    }, 1500);
  };

  // For the dropdown: vendor can pick "All Branches" (null) or a specific branch; super_admin sees branches only
  const allBranchesOption = { id: null, name: 'All Branches' };
  const branchDropdownOptions = userRole === 'super_admin' ? [...branches] : [allBranchesOption, ...branches];
  const filteredTenants = branchDropdownOptions.filter((b) =>
    b?.name?.toLowerCase()?.includes(tenantSearch?.toLowerCase())
  );
  const displayBranchLabel = selectedBranch?.name || 'All Branches';

  const isActive = (path) => location?.pathname === path;

  const formatLastUpdated = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 min ago';
    return `${diffMins} mins ago`;
  };

  return (
    <>
      <header className="nav-header">
        {/* Logo */}
        <Link to="/executive-overview" className="nav-logo-container">
          <div className="nav-logo-icon">
            <Icon name="Zap" size={20} color="#FFFFFF" strokeWidth={2.5} />
          </div>
          <span className="nav-logo-text hidden sm:block">
            Fit<span>Pulse</span>
          </span>
        </Link>

        {/* Primary Navigation - Desktop */}
        <nav
          className="hidden lg:flex items-center gap-1 flex-1 ml-6"
          aria-label="Primary navigation"
        >
          {visibleNavItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              aria-current={isActive(item?.path) ? 'page' : undefined}
              title={item?.tooltip}
              className={`
                flex items-center gap-2 px-4 h-10 rounded-md text-sm font-medium font-caption
                transition-all duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)]
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1
                ${isActive(item?.path)
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }
              `}
            >
              <Icon
                name={item?.icon}
                size={16}
                color={isActive(item?.path) ? '#FFFFFF' : 'currentColor'}
                strokeWidth={isActive(item?.path) ? 2.5 : 2}
              />
              {item?.label}
            </Link>
          ))}
        </nav>

        {/* Right Controls */}
        <div className="flex items-center gap-2 ml-auto">
          {/* Global Date Range Filter */}
          <div ref={dateRef} className="relative hidden md:block">
            {/* <button
              onClick={() => {
                setDateDropdownOpen(!dateDropdownOpen);
                setTenantDropdownOpen(false);
                setRoleDropdownOpen(false);
              }}
              className={`
                flex items-center gap-2 px-3 h-9 rounded-md border text-sm font-caption font-medium
                transition-all duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)]
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1
                ${dateDropdownOpen
                  ? 'border-primary text-primary bg-primary/5' :'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground bg-card'
                }
              `}
              aria-expanded={dateDropdownOpen}
              aria-haspopup="listbox"
            >
              <Icon name="Calendar" size={14} color="currentColor" />
              <span>{selectedDatePreset?.label}</span>
              <Icon
                name="ChevronDown"
                size={12}
                color="currentColor"
                className={`transition-transform duration-[250ms] ${dateDropdownOpen ? 'rotate-180' : ''}`}
              />
            </button> */}

            {/* {dateDropdownOpen && (
              <div
                className="absolute top-full right-0 mt-2 w-48 bg-card border border-border rounded-md shadow-dropdown z-[50] animate-slide-down overflow-hidden"
                role="listbox"
                aria-label="Date range presets"
              >
                {DATE_PRESETS?.map((preset) => (
                  <button
                    key={preset?.value}
                    role="option"
                    aria-selected={selectedDatePreset?.value === preset?.value}
                    onClick={() => handleDatePresetSelect(preset)}
                    className={`
                      w-full flex items-center justify-between px-4 py-2.5 text-sm font-caption
                      transition-colors duration-[150ms]
                      ${selectedDatePreset?.value === preset?.value
                        ? 'bg-primary/10 text-primary font-medium' :'text-foreground hover:bg-muted'
                      }
                    `}
                  >
                    <span className="font-medium">{preset?.label}</span>
                    <span className="text-xs text-muted-foreground">{preset?.description}</span>
                  </button>
                ))}
              </div>
            )} */}
          </div>

          {/* Gym ID Badge — vendor only */}
          {userRole === 'vendor' && profile?.gym_id && (
            <GymIdBadge gymId={profile.gym_id} />
          )}

          {/* Branch Selector */}
          {showBranchDropdown && (
            <div ref={tenantRef} className="relative hidden md:block">
              <button
                onClick={() => {
                  setTenantDropdownOpen(!tenantDropdownOpen);
                  setDateDropdownOpen(false);
                  setRoleDropdownOpen(false);
                }}
                className={`
                  flex items-center gap-2 px-3 h-9 rounded-md border text-sm font-caption font-medium
                  max-w-[160px] transition-all duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)]
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1
                  ${tenantDropdownOpen
                    ? 'border-primary text-primary bg-primary/5' : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground bg-card'
                  }
                `}
                aria-expanded={tenantDropdownOpen}
                aria-haspopup="listbox"
              >
                <Icon name="Building2" size={14} color="currentColor" />
                <span className="truncate">{displayBranchLabel}</span>
                <Icon
                  name="ChevronDown"
                  size={12}
                  color="currentColor"
                  className={`flex-shrink-0 transition-transform duration-[250ms] ${tenantDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {tenantDropdownOpen && (
                <div
                  className="absolute top-full right-0 mt-2 w-56 bg-card border border-border rounded-md shadow-dropdown z-[50] animate-slide-down overflow-hidden"
                  role="listbox"
                  aria-label="Select branch"
                >
                  <div className="p-2 border-b border-border">
                    <div className="flex items-center gap-2 px-2 py-1.5 bg-muted rounded-sm">
                      <Icon name="Search" size={13} color="var(--color-muted-foreground)" />
                      <input
                        type="text"
                        placeholder="Search branches..."
                        value={tenantSearch}
                        onChange={(e) => setTenantSearch(e?.target?.value)}
                        className="flex-1 bg-transparent text-xs font-caption text-foreground placeholder:text-muted-foreground outline-none"
                        aria-label="Search branches"
                      />
                    </div>
                  </div>
                  <div className="max-h-48 overflow-y-auto">
                    {filteredTenants?.map((branch) => {
                      const isSelected = branch.id === (selectedBranch?.id || null);
                      return (
                        <button
                          key={branch.id ?? 'all'}
                          role="option"
                          aria-selected={isSelected}
                          onClick={() => handleTenantSelect(branch.id === null ? null : branch)}
                          className={`
                            w-full flex items-center gap-2 px-4 py-2.5 text-sm font-caption text-left
                            transition-colors duration-[150ms]
                            ${isSelected ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-muted'}
                          `}
                        >
                          <Icon
                            name={isSelected ? 'CheckCircle2' : 'Circle'}
                            size={14}
                            color={isSelected ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                          />
                          {branch.name}
                        </button>
                      );
                    })}
                    {filteredTenants?.length === 0 && (
                      <div className="px-4 py-3 text-xs text-muted-foreground font-caption text-center">
                        No branches found
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Static branch label for staff/trainer */}
          {showBranchLabel && (
            <div className="hidden md:flex items-center gap-2 px-3 h-9 rounded-md border border-border bg-card text-sm font-caption font-medium text-muted-foreground">
              <Icon name="Building2" size={14} color="currentColor" />
              <span className="truncate max-w-[120px]">{selectedBranch.name}</span>
            </div>
          )}

          {/* User Menu */}
          <div ref={userMenuRef} className="relative hidden sm:block">
            <button
              onClick={() => {
                const opening = !userMenuOpen;
                setUserMenuOpen(opening);
                setTenantDropdownOpen(false);
                setDateDropdownOpen(false);
                if (opening) fetchBilling();
              }}
              className={`
                flex items-center gap-2 px-2 h-9 rounded-md border text-sm font-caption font-medium
                transition-all duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)]
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1
                ${userMenuOpen ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 bg-card'}
              `}
              aria-expanded={userMenuOpen}
              aria-haspopup="menu"
            >
              <div className="w-6 h-6 rounded-full bg-[var(--color-primary)] flex items-center justify-center flex-shrink-0">
                <span className="text-[10px] font-bold text-white leading-none">{getUserInitials()}</span>
              </div>
              <span className="text-foreground max-w-[120px] truncate">{profile?.full_name || 'User'}</span>
              <Icon
                name="ChevronDown"
                size={12}
                color="var(--color-muted-foreground)"
                className={`transition-transform duration-[250ms] ${userMenuOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {userMenuOpen && (
              <div
                className={`absolute top-full right-0 mt-2 bg-card border border-border rounded-md shadow-dropdown z-[50] animate-slide-down overflow-hidden ${userRole === 'vendor' ? 'w-72' : 'w-52'}`}
                role="menu"
              >
                {/* Header */}
                <div className="px-3 py-2.5 border-b border-border">
                  <p className="text-sm font-semibold font-caption text-foreground truncate">{profile?.full_name || 'User'}</p>
                  <p className="text-xs text-muted-foreground font-caption truncate">{profile?.email || ''}</p>
                  <span className="inline-block mt-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-[var(--color-primary)]/10 text-[var(--color-primary)] font-caption">
                    {ROLE_LABELS[userRole] || userRole}
                  </span>
                  {userRole === 'vendor' && profile?.gym_id && (
                    <span className="inline-block mt-1 ml-1 px-1.5 py-0.5 rounded text-[10px] font-data text-muted-foreground border border-border">
                      {profile.gym_id}
                    </span>
                  )}
                </div>

                {/* Menu Items */}
                <div className="py-1">
                  {userRole === 'vendor' && (
                    <Link
                      to="/gym-setup"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2.5 px-3 py-2 text-sm font-caption text-foreground hover:bg-muted transition-colors"
                    >
                      <Icon name="Settings2" size={14} color="var(--color-muted-foreground)" />
                      Gym Settings
                    </Link>
                  )}
                  <Link
                    to="/profile-update-page"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2 text-sm font-caption text-foreground hover:bg-muted transition-colors"
                  >
                    <Icon name="User" size={14} color="var(--color-muted-foreground)" />
                    Profile
                  </Link>
                  <Link
                    to="/account-settings"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2 text-sm font-caption text-foreground hover:bg-muted transition-colors"
                  >
                    <Icon name="SlidersHorizontal" size={14} color="var(--color-muted-foreground)" />
                    Account Settings
                  </Link>
                  <Link
                    to="/change-password"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-2.5 px-3 py-2 text-sm font-caption text-foreground hover:bg-muted transition-colors"
                  >
                    <Icon name="KeyRound" size={14} color="var(--color-muted-foreground)" />
                    Change Password
                  </Link>
                </div>

                {/* Billing Section — vendor only */}
                {userRole === 'vendor' && (
                  <div className="border-t border-border px-3 py-2.5">
                    <p className="text-[10px] font-semibold font-caption text-muted-foreground uppercase tracking-wider mb-2">
                      Billing
                    </p>

                    {billingLoading ? (
                      <div className="flex items-center gap-2 py-1">
                        <Icon name="Loader2" size={12} color="var(--color-muted-foreground)" className="animate-spin" />
                        <span className="text-xs font-caption text-muted-foreground">Loading...</span>
                      </div>
                    ) : billing?.subscription ? (
                      <>
                        {/* Plan + status */}
                        {(() => {
                          const sub    = billing.subscription;
                          const status = sub.payment_status;
                          const isActive  = status === 'active';
                          const isTrial   = status === 'trial';
                          const badgeClass = isActive
                            ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]'
                            : isTrial
                            ? 'bg-[var(--color-warning)]/10 text-[var(--color-warning)]'
                            : 'bg-[var(--color-destructive)]/10 text-[var(--color-destructive)]';
                          const badgeLabel = isActive ? 'Active' : isTrial ? 'Trial' : 'Expired';
                          const endDate    = sub.end_date
                            ? new Date(sub.end_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
                            : null;

                          return (
                            <div className="mb-2.5">
                              <div className="flex items-center justify-between mb-0.5">
                                <span className="text-xs font-semibold font-caption text-foreground truncate mr-2">
                                  {sub.plan_name}
                                </span>
                                <span className={`shrink-0 text-[10px] font-semibold font-caption px-1.5 py-0.5 rounded-full ${badgeClass}`}>
                                  {badgeLabel}
                                </span>
                              </div>
                              {endDate && (
                                <p className="text-[10px] font-caption text-muted-foreground">
                                  {isActive ? 'Renews' : isTrial ? 'Trial ends' : 'Expired'} {endDate}
                                </p>
                              )}
                            </div>
                          );
                        })()}

                        {/* Invoice history */}
                        {billing.invoices?.length > 0 && (
                          <div className="space-y-1 mb-2.5">
                            {billing.invoices.slice(0, 4).map(inv => {
                              const isPaid    = inv.invoice_status === 'paid';
                              const isOverdue = inv.invoice_status === 'overdue';
                              const amount    = parseFloat(inv.total_amount || inv.amount || 0);
                              const date      = inv.paid_at || inv.created_at;
                              return (
                                <div key={inv.id} className="flex items-center justify-between gap-2">
                                  <div className="flex items-center gap-1.5 min-w-0">
                                    <Icon
                                      name={isPaid ? 'CheckCircle2' : isOverdue ? 'AlertCircle' : 'Clock'}
                                      size={11}
                                      color={isPaid ? 'var(--color-success)' : isOverdue ? 'var(--color-destructive)' : 'var(--color-warning)'}
                                    />
                                    <span className="text-[10px] font-data text-muted-foreground truncate">{inv.invoice_number}</span>
                                    {date && (
                                      <span className="text-[10px] font-caption text-muted-foreground shrink-0">
                                        · {new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-[10px] font-semibold font-data text-foreground shrink-0">
                                    ₹{amount > 0 ? amount.toLocaleString('en-IN', { maximumFractionDigits: 0 }) : '—'}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-2 pt-1">
                          <Link
                            to="/vendor-plan-selection"
                            onClick={() => setUserMenuOpen(false)}
                            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-[10px] font-semibold font-caption bg-[var(--color-primary)]/10 text-[var(--color-primary)] rounded hover:bg-[var(--color-primary)]/20 transition-colors"
                          >
                            <Icon name="RefreshCw" size={10} color="var(--color-primary)" />
                            {billing.subscription?.payment_status === 'active' ? 'Change Plan' : 'Renew / Upgrade'}
                          </Link>
                          {billing.invoices?.length > 0 && (
                            <Link
                              to="/vendor-plan-selection"
                              onClick={() => setUserMenuOpen(false)}
                              className="flex items-center gap-1 px-2 py-1.5 text-[10px] font-caption text-muted-foreground border border-border rounded hover:bg-muted transition-colors"
                            >
                              <Icon name="Receipt" size={10} color="var(--color-muted-foreground)" />
                              All
                            </Link>
                          )}
                        </div>
                      </>
                    ) : (
                      <Link
                        to="/vendor-plan-selection"
                        onClick={() => setUserMenuOpen(false)}
                        className="flex items-center gap-1.5 text-xs font-caption text-[var(--color-primary)] hover:underline"
                      >
                        <Icon name="CreditCard" size={12} color="var(--color-primary)" />
                        Choose a plan to get started
                      </Link>
                    )}
                  </div>
                )}

                <div className="border-t border-border py-1">
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-caption text-[var(--color-error)] hover:bg-[var(--color-error)]/5 transition-colors text-left"
                  >
                    <Icon name="LogOut" size={14} color="var(--color-error)" />
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Real-Time Status Indicator */}
          <div className="relative hidden sm:flex items-center">
            <button
              onMouseEnter={() => setStatusTooltipVisible(true)}
              onMouseLeave={() => setStatusTooltipVisible(false)}
              onFocus={() => setStatusTooltipVisible(true)}
              onBlur={() => setStatusTooltipVisible(false)}
              onClick={handleRefresh}
              className="flex items-center gap-1.5 px-2 h-9 rounded-md hover:bg-muted transition-colors duration-[150ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              aria-label={`Data status: ${connectionStatus}. Last updated ${formatLastUpdated(lastUpdated)}. Click to refresh.`}
            >
              <span
                className={`status-dot ${connectionStatus}`}
                aria-hidden="true"
              />
              <Icon
                name="RefreshCw"
                size={14}
                color="var(--color-muted-foreground)"
                className={connectionStatus === 'syncing' ? 'animate-spin' : ''}
              />
            </button>

            {statusTooltipVisible && (
              <div
                className="absolute top-full right-0 mt-2 w-48 bg-card border border-border rounded-md shadow-dropdown z-[50] p-3 animate-fade-in"
                role="tooltip"
              >
                <div className="flex items-center gap-2 mb-1">
                  <span className={`status-dot ${connectionStatus}`} aria-hidden="true" />
                  <span className="text-xs font-medium font-caption text-foreground capitalize">
                    {connectionStatus === 'online' ? 'Live Data' : connectionStatus === 'syncing' ? 'Syncing...' : 'Offline'}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground font-caption">
                  Updated {formatLastUpdated(lastUpdated)}
                </p>
                <p className="text-xs text-primary font-caption mt-1 cursor-pointer">
                  Click to refresh
                </p>
              </div>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden flex items-center justify-center w-10 h-10 rounded-md hover:bg-muted transition-colors duration-[150ms] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
            aria-label={mobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
          >
            <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={20} color="var(--color-foreground)" />
          </button>
        </div>
      </header>
      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[99] bg-foreground/20"
          aria-hidden="true"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
      {/* Mobile Menu Panel */}
      <div
        id="mobile-menu"
        ref={mobileMenuRef}
        className={`
          lg:hidden fixed top-[60px] left-0 right-0 z-[100] bg-card border-b border-border shadow-lg
          transition-all duration-[250ms] ease-[cubic-bezier(0.4,0,0.2,1)]
          ${mobileMenuOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'}
        `}
        aria-label="Mobile navigation"
        role="navigation"
      >
        <div className="p-4 space-y-1">
          {/* Nav Items */}
          {visibleNavItems?.map((item) => (
            <Link
              key={item?.path}
              to={item?.path}
              aria-current={isActive(item?.path) ? 'page' : undefined}
              className={`
                flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium font-caption
                transition-all duration-[250ms] min-h-[48px]
                ${isActive(item?.path)
                  ? 'bg-primary text-primary-foreground'
                  : 'text-foreground hover:bg-muted'
                }
              `}
            >
              <Icon
                name={item?.icon}
                size={18}
                color={isActive(item?.path) ? '#FFFFFF' : 'currentColor'}
              />
              <div>
                <div>{item?.label}</div>
                <div className={`text-xs mt-0.5 ${isActive(item?.path) ? 'text-primary-foreground/80' : 'text-muted-foreground'}`}>
                  {item?.tooltip}
                </div>
              </div>
            </Link>
          ))}

          {/* Divider */}
          <div className="border-t border-border my-3" />

          {/* Mobile Controls */}
          <div className="space-y-3 px-1">
            {/* Date Preset */}
            {/* <div>
              <p className="text-xs text-muted-foreground font-caption mb-2 px-3">Date Range</p>
              <div className="flex gap-2 flex-wrap px-1">
                {DATE_PRESETS?.map((preset) => (
                  <button
                    key={preset?.value}
                    onClick={() => handleDatePresetSelect(preset)}
                    className={`
                      px-3 py-1.5 rounded-sm text-xs font-caption font-medium min-h-[36px]
                      transition-colors duration-[150ms]
                      ${selectedDatePreset?.value === preset?.value
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-muted-foreground hover:text-foreground'
                      }
                    `}
                    title={preset?.description}
                  >
                    {preset?.label}
                  </button>
                ))}
              </div>
            </div> */}

            {/* Branch Selector Mobile */}
            {showBranchDropdown && (
              <div className="px-1">
                <p className="text-xs text-muted-foreground font-caption mb-2 px-2">Branch</p>
                <div className="space-y-1">
                  {branchDropdownOptions?.map((branch) => {
                    const isSelected = branch.id === (selectedBranch?.id || null);
                    return (
                      <button
                        key={branch.id ?? 'all'}
                        onClick={() => handleTenantSelect(branch.id === null ? null : branch)}
                        className={`
                          w-full flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-caption text-left min-h-[44px]
                          transition-colors duration-[150ms]
                          ${isSelected ? 'bg-primary/10 text-primary font-medium' : 'text-foreground hover:bg-muted'}
                        `}
                      >
                        <Icon
                          name={isSelected ? 'CheckCircle2' : 'Building2'}
                          size={15}
                          color={isSelected ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
                        />
                        {branch.name}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {showBranchLabel && (
              <div className="px-1">
                <p className="text-xs text-muted-foreground font-caption mb-2 px-2">Branch</p>
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-md bg-muted/40">
                  <Icon name="Building2" size={15} color="var(--color-primary)" />
                  <span className="text-sm font-caption text-foreground">{selectedBranch.name}</span>
                </div>
              </div>
            )}

            {/* User Info */}
            <div className="px-3 py-2.5 bg-muted rounded-md">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-[var(--color-primary)] flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-white leading-none">{getUserInitials()}</span>
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold font-caption text-foreground truncate">{profile?.full_name || 'User'}</p>
                  <p className="text-xs text-muted-foreground font-caption">{ROLE_LABELS[userRole] || userRole}</p>
                  {userRole === 'vendor' && profile?.gym_id && (
                    <p className="text-xs font-data text-primary mt-0.5">{profile.gym_id}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Mobile User Menu Links */}
            {userRole === 'vendor' && (
              <Link
                to="/gym-owner-portal"
                className="flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium font-caption text-foreground hover:bg-muted transition-colors min-h-[48px]"
              >
                <Icon name="Settings2" size={18} color="currentColor" />
                Gym Settings
              </Link>
            )}
            <Link
              to="/profile-update-page"
              className="flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium font-caption text-foreground hover:bg-muted transition-colors min-h-[48px]"
            >
              <Icon name="User" size={18} color="currentColor" />
              Profile
            </Link>
            <Link
              to="/account-settings"
              className="flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium font-caption text-foreground hover:bg-muted transition-colors min-h-[48px]"
            >
              <Icon name="SlidersHorizontal" size={18} color="currentColor" />
              Account Settings
            </Link>
            <Link
              to="/change-password"
              className="flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium font-caption text-foreground hover:bg-muted transition-colors min-h-[48px]"
            >
              <Icon name="KeyRound" size={18} color="currentColor" />
              Change Password
            </Link>
            <button
              onClick={handleSignOut}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-md text-sm font-medium font-caption text-[var(--color-error)] hover:bg-[var(--color-error)]/5 transition-colors min-h-[48px]"
            >
              <Icon name="LogOut" size={18} color="var(--color-error)" />
              Sign Out
            </button>

            {/* Status Row */}
            <div className="flex items-center justify-between px-3 py-2">
              <div className="flex items-center gap-1.5">
                <span className={`status-dot ${connectionStatus}`} aria-hidden="true" />
                <span className="text-xs text-muted-foreground font-caption capitalize">
                  {connectionStatus === 'online' ? 'Live' : connectionStatus === 'syncing' ? 'Syncing...' : 'Offline'}
                </span>
              </div>
              <button
                onClick={handleRefresh}
                className="text-xs text-primary font-caption hover:underline"
                aria-label="Refresh data"
              >
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* Spacer to prevent content overlap */}
      <div className="h-[60px]" aria-hidden="true" />
    </>
  );
}